import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext, useState } from 'react';

import { ApiError } from '../api/errors';
import { fetchMe, login as loginRequest, logout as logoutRequest } from './api';
import type { Owner } from './owner';

export const SESSION_QUERY_KEY = ['session'] as const;

export type SessionStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'error';

/** Why the owner is being asked to sign in again, distinct from "never signed in yet". Shown
 * once on the sign-in screen so an expiry mid-session reads differently from a fresh visit. */
export type SignInReason = 'expired' | null;

export interface SessionContextValue {
  readonly status: SessionStatus;
  readonly owner: Owner | null;
  readonly signInReason: SignInReason;
  readonly bootstrapError: ApiError | null;
  readonly login: (credentials: { email: string; password: string }) => Promise<void>;
  readonly logout: () => Promise<void>;
  readonly isLoggingIn: boolean;
  readonly loginError: ApiError | null;
  readonly retryBootstrap: () => Promise<void>;
  readonly clearSignInReason: () => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export interface SessionProviderProps {
  readonly children: ReactNode;
}

/**
 * Session bootstrap and mutation boundary for the whole app. `GET /me` on mount is what
 * decides signed-in vs. signed-out (`docs/implementation/06-mobile-web.md` 6.1); a `401`
 * discovered on a *later* bootstrap (the query refetches on window focus) is treated as an
 * expired session rather than a fresh signed-out visit, because the previous cache entry was
 * a real owner.
 */
export function SessionProvider({ children }: SessionProviderProps) {
  const queryClient = useQueryClient();
  const [signInReason, setSignInReason] = useState<SignInReason>(null);

  const sessionQuery = useQuery<Owner | null>({
    queryKey: SESSION_QUERY_KEY,
    queryFn: async () => {
      try {
        return await fetchMe();
      } catch (error) {
        if (error instanceof ApiError && error.isSessionInvalid) {
          const previous = queryClient.getQueryData<Owner | null>(SESSION_QUERY_KEY);
          if (previous) {
            setSignInReason('expired');
          }
          return null;
        }
        throw error;
      }
    },
  });

  const loginMutation = useMutation({
    mutationFn: loginRequest,
    onSuccess: (owner) => {
      setSignInReason(null);
      queryClient.setQueryData(SESSION_QUERY_KEY, owner);
    },
  });

  const logoutMutation = useMutation({
    mutationFn: logoutRequest,
    onSettled: () => {
      queryClient.setQueryData(SESSION_QUERY_KEY, null);
    },
  });

  let status: SessionStatus;
  if (sessionQuery.isPending) {
    status = 'loading';
  } else if (sessionQuery.isError) {
    status = 'error';
  } else if (sessionQuery.data) {
    status = 'authenticated';
  } else {
    status = 'unauthenticated';
  }

  const bootstrapError = sessionQuery.error instanceof ApiError ? sessionQuery.error : null;
  const loginError = loginMutation.error instanceof ApiError ? loginMutation.error : null;

  const value: SessionContextValue = {
    status,
    owner: sessionQuery.data ?? null,
    signInReason,
    bootstrapError,
    login: async (credentials) => {
      await loginMutation.mutateAsync(credentials);
    },
    logout: async () => {
      await logoutMutation.mutateAsync();
    },
    isLoggingIn: loginMutation.isPending,
    loginError,
    retryBootstrap: async () => {
      await sessionQuery.refetch();
    },
    clearSignInReason: () => {
      setSignInReason(null);
    },
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/** Read the current session state. Must be used under `<SessionProvider>`. */
export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (context === null) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
}
